import { Injectable } from '@angular/core';
import * as dayjs from 'dayjs';
import { Subject } from 'rxjs';
import { RepositoryService } from '../baseServices/repository.service';
import { ActivityLogFilterDataDto } from '../dtos/activity-log-service-dtos';
import { ActivityLog } from '../entities/ActivityLog';
import { Project } from '../entities/Project';
import { StoreName } from '../entities/StoreName';
import { Team, TeamType } from '../entities/Team';
import { DataChangeEmitPayload, DataChangeEmitWhat, DataChangeEmitWho } from '../models/data-change-emit/data-change-emit-payload';
import { DbTransaction } from '../models/db/db-transaction';
import { Callback, CallbackNames } from '../models/db/db-transaction-callback';
import { Check, EntityHelper } from '../utils/entity-helper';
import { RecordNotFoundException, RecordSoftDeletedException } from '../utils/exceptions';
import { ActivityLogService } from './activityLog.service';
import { ProjectService } from './project.service';
import { SyncNotificationService } from './syncNotificationService';
import { TeamUserService } from './team-user.service';
import { TimeManagerProjectService } from './time-manager-project.service';
import { TimeManagerTeamService } from './time-manager-team.service';
import { UserService } from './user.service';
@Injectable({
  providedIn: 'root',
})
export class TeamService {
  bgClass = 'team-detail-options__in-prgress';
  private onTeamProjectDataDownload = new Subject();
  onTeamProjectDataDownload$ = this.onTeamProjectDataDownload.asObservable();

  private emitChangeSource = new Subject<DataChangeEmitPayload<Team>>();
  changeEmitted$ = this.emitChangeSource.asObservable();

  constructor(
    private repository: RepositoryService,
    private syncNotificationService: SyncNotificationService,
    private activityLogService: ActivityLogService,
    private projectService: ProjectService,
    private userService: UserService,
    private teamUserService: TeamUserService,
    private timeManagerProjectService: TimeManagerProjectService,
    private timeManagerTeamService: TimeManagerTeamService
  ) {
    this.syncNotificationService.onStatusChange$.subscribe(async (data: any) => {
      if (data && data.Status === 'Completed' && data.EntityName.toLowerCase() === (StoreName.ActivityLog.toLowerCase() || StoreName.TimeRecord.toLowerCase()) && data.syncProcess === 'download') {
        this.onTeamProjectDataDownload.next(undefined);
      }
    });
  }

  // #region Utility

  emitChange(team: Team, who: DataChangeEmitWho, what: DataChangeEmitWhat) {
    this.emitChangeSource.next({ data: team, who: who, what: what });
  }

  getEmitChangeCallback(team: Team, who: DataChangeEmitWho, what: DataChangeEmitWhat): Callback {
    return {
      name: CallbackNames.EmitChangeForTeamUser + '_' + team.RecordId,
      fn: this.emitChange.bind(this, team, who, what),
    };
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return (
      existingTransaction ||
      (await this.repository.startTransaction([StoreName.TimeManager2Project, StoreName.TimeManager2Team, StoreName.Team, StoreName.Team2User, StoreName.ActivityLog, StoreName.Project], 'readonly'))
    );
  }

  // #endregion

  async getTeams(existingTransaction?: DbTransaction): Promise<Team[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    return await this.repository.getAll(StoreName.Team, undefined, undefined, transaction);
  }

  async getTeamById(teamId: number, existingTransaction?: DbTransaction): Promise<Team> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    return await this.repository.getByOnlineId(StoreName.Team, teamId, undefined, transaction);
  }

  async getTeamsByIds(teamIds: number[], existingTransaction?: DbTransaction): Promise<Team[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const promises = teamIds.map(id => this.getTeamById(id, transaction));
    return await Promise.all(promises);
  }

  async getManagedTeamIds(managerId = this.userService.getCurrentUserId(), existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const timeManager2Teams = await this.timeManagerTeamService.getTimeManagerTeamsByUserId(managerId, transaction, includeDeleted);
    const managedTeamIds = timeManager2Teams.map(timeManager2Team => timeManager2Team.TeamId);
    return managedTeamIds;
  }

  async getManagedTeams(managerId = this.userService.getCurrentUserId(), existingTransaction?: DbTransaction): Promise<Team[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const managedTeamIds = await this.getManagedTeamIds(managerId, transaction);
    const managedTeams = await this.getTeamsByIds(managedTeamIds, transaction);
    return managedTeams;
  }

  checkIsPersonalTeam(team: Team): boolean {
    return team.EntityTypeId === TeamType.Personal;
  }

  checkIsProjectTeam(team: Team): boolean {
    return team.EntityTypeId === TeamType.Project;
  }

  async getManagedPersonalTeams(managerId = this.userService.getCurrentUserId(), existingTransaction?: DbTransaction): Promise<Team[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const managedTeams = await this.getManagedTeams(managerId, transaction);
    const personalTeams = managedTeams.filter(team => this.checkIsPersonalTeam(team));
    return personalTeams;
  }

  async getManagedProjectTeams(managerId = this.userService.getCurrentUserId(), existingTransaction?: DbTransaction): Promise<Team[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const managedTeams = await this.getManagedTeams(managerId, transaction);
    const projectTeams = managedTeams.filter(team => this.checkIsProjectTeam(team));
    return projectTeams;
  }

  async getManagedTeamActivityLogs(managerId = this.userService.getCurrentUserId(), existingTransaction?: DbTransaction): Promise<ActivityLog[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const managedUserIds = await this.getManagedTeamUserIds(managerId, transaction);
    return await this.activityLogService.getActivityLogsByUserIds(managedUserIds, transaction);
  }

  async getManagedProjectIds(managerId = this.userService.getCurrentUserId(), existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const timeManager2Project = await this.timeManagerProjectService.getTimeManagerProjectsByUserId(managerId, transaction, includeDeleted);
    const managedProjectIds = timeManager2Project.map(timeManager2Project => timeManager2Project.ProjectId);
    return managedProjectIds;
  }

  async getManagedProjectActivityLogs(
    projectId: number = undefined,
    managerId = this.userService.getCurrentUserId(),
    startDate = dayjs(),
    endDate = dayjs(),
    existingTransaction?: DbTransaction
  ): Promise<ActivityLog[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const managedProjectIds = projectId ? [projectId] : await this.getManagedProjectIds(managerId, transaction);

    let allActivityLogsForProjectIds = await this.activityLogService.getActivityLogsByProjectIds(managedProjectIds, transaction);

    const filter: ActivityLogFilterDataDto = {
      projectIds: managedProjectIds,
      excludedUserId: managerId,
      activityDateMin: startDate,
      activityDateMax: endDate,
      isDeleted: false,
    };
    return this.activityLogService.filterActivityLogs(allActivityLogsForProjectIds, filter);
  }

  async getManagedActivityLogs(managerId = this.userService.getCurrentUserId(), existingTransaction?: DbTransaction): Promise<ActivityLog[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const managedTeamActivityLogs = await this.getManagedTeamActivityLogs(managerId, transaction);
    const managedProjectActivityLogs = await this.getManagedProjectActivityLogs(undefined, managerId, undefined, undefined, transaction);

    var managedTeamActivityLogIds = new Set(managedTeamActivityLogs.map(activityLog => activityLog.Id));
    var managedActivityLogs = [...managedTeamActivityLogs, ...managedProjectActivityLogs.filter(activityLog => !managedTeamActivityLogIds.has(activityLog.Id))];
    return managedActivityLogs;
  }

  async getActivityLogsForProjectTeam(
    team: Team,
    managerId = this.userService.getCurrentUserId(),
    startDate = dayjs(),
    endDate = dayjs(),
    existingTransaction?: DbTransaction
  ): Promise<ActivityLog[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const projectId = team.EntityId;
    const userIds = await this.getTeamUserIdsByTeamIds([team.Id], managerId, transaction);

    let activityLogsForUserIdsDateRange = await this.activityLogService.getActivityLogsByUserIdsDateRange(userIds, startDate, endDate, transaction);

    const filter: ActivityLogFilterDataDto = {
      projectId: projectId,
      userIds: userIds,
      activityDateMin: startDate,
      activityDateMax: endDate,
      isDeleted: false,
    };
    return this.activityLogService.filterActivityLogs(activityLogsForUserIdsDateRange, filter);
  }

  async getActivityLogsForPersonalTeam(
    team: Team,
    managerId = this.userService.getCurrentUserId(),
    startDate = dayjs(),
    endDate = dayjs(),
    existingTransaction?: DbTransaction
  ): Promise<ActivityLog[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const userIds = await this.getTeamUserIdsByTeamIds([team.Id], managerId, transaction);
    return await this.activityLogService.getActivityLogsByUserIdsDateRange(userIds, startDate, endDate, transaction);
  }

  async getActivityLogsForTeam(team: Team, startDate = dayjs(), endDate = dayjs()): Promise<ActivityLog[]> {
    if (this.checkIsProjectTeam(team)) {
      return await this.getActivityLogsForProjectTeam(team, undefined, startDate, endDate);
    } else if (this.checkIsPersonalTeam(team)) {
      return await this.getActivityLogsForPersonalTeam(team, undefined, startDate, endDate);
    }
  }

  async getManagedProjects(managerId = this.userService.getCurrentUserId(), existingTransaction?: DbTransaction): Promise<Project[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const managedProjectIds = await this.getManagedProjectIds(managerId, transaction);
    const managedProjects = await this.projectService.getProjectsByOnlineIds(managedProjectIds, transaction);
    return managedProjects.filter(Boolean);
  }

  async getTeamUserIdsByTeamIds(teamIds: number[], excludedUserId: number = null, existingTransaction?: DbTransaction): Promise<number[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const promises = teamIds.map(teamId => this.teamUserService.getAllTeamUsersByTeamId(teamId, transaction));
    const results = await Promise.all(promises);
    let team2Users = results.reduce((acc, val) => acc.concat(val), []);

    if (excludedUserId) {
      team2Users = team2Users.filter(team2User => team2User.UserId !== excludedUserId);
    }

    const userIds = [...new Set(team2Users.map(team2User => team2User.UserId))];
    return userIds;
  }

  async getManagedTeamUserIds(managerId = this.userService.getCurrentUserId(), existingTransaction?: DbTransaction): Promise<number[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const managedTeamIds = await this.getManagedTeamIds(managerId, transaction);
    const managedUserIds = await this.getTeamUserIdsByTeamIds(managedTeamIds, managerId, transaction);
    return managedUserIds;
  }

  async getManagedProjectTeamsByProjectId(projectId: number): Promise<Team[]> {
    const allProjectTeams = await this.getManagedProjectTeams();
    const projectTeams = allProjectTeams.filter(team => team.EntityId === projectId);
    return projectTeams;
  }

  // #region Low-level Get One

  async getTeamByOnlineId(onlineId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Team> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByOnlineId<Team>(StoreName.Team, onlineId, Team, transaction);

    if (result === null) {
      throw new RecordNotFoundException(onlineId);
    }

    const record = result as Team;

    if (this.checkIsTeamSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(onlineId);
    }

    return record;
  }

  async getTeamByRecordId(recordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Team> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByLocalId<Team>(StoreName.Team, recordId, Team, transaction);

    if (result === null) {
      throw new RecordNotFoundException(recordId);
    }

    const record = result as Team;

    if (this.checkIsTeamSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(recordId);
    }

    return record;
  }

  // #endregion
  // #endregion
  // region Low-level Create

  private async createTeam(who: DataChangeEmitWho, recordToCreate: Team, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LC1. Validate action
    await this.validateTeamCreation(transaction);

    // TODO: Impelment LC2 and LC3

    // LC4. Repository call
    const recordId = await this.repository.create(recordToCreate, StoreName.Team, transaction);

    // LC5. Get created record
    const createdRecord = await this.getTeamByRecordId(recordId, transaction, includeDeleted);

    // LC6. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(createdRecord, who, DataChangeEmitWhat.Create));

    // LC7. Return id
    return recordId;
  }

  // #endregion
  // region Low-level Update

  async updateTeam(who: DataChangeEmitWho, recordToUpdate: Team, skipLocalCopyValidation: boolean, localCopy: Team | null, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LU1. Get existing record
    const existingRecord = await this.getTeamByRecordId(recordToUpdate.RecordId, transaction);

    // LU2. Validate action
    await this.validateTeamUpdate(recordToUpdate, transaction);

    // LU3. Idempotent check
    if (EntityHelper.checkAllFieldsEqual(existingRecord, recordToUpdate)) {
      return;
    }

    // LU4. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<Team>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy<Team>(localCopy, existingRecord, checks);
    }

    // LU5. Validate model
    await this.validateTeam(recordToUpdate, transaction);

    // LU6. Repository call
    await this.repository.update(recordToUpdate, StoreName.Team, transaction);

    // LU7. Get updated record
    const updatedRecord = await this.getTeamByRecordId(recordToUpdate.RecordId, transaction);

    // LU8. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(updatedRecord, who, DataChangeEmitWhat.Update));

    // LU9. Return
    return;
  }

  // #endregion
  // region Low-level Validate Model

  async validateTeam(team: Team, existingTransaction?: DbTransaction): Promise<void> {
    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Low-level Validate Action

  async validateTeamCreation(existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  async validateTeamUpdate(team: Team, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Mid-level Sync

  async upsertTeamFromSync(who: DataChangeEmitWho, recordToUpsert: Team, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    try {
      const existingRecord = await this.getTeamByOnlineId(recordToUpsert.Id, transaction, true);
      recordToUpsert.RecordId = existingRecord.RecordId;
      await this.updateTeam(who, recordToUpsert, true, null, transaction);
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        await this.createTeam(who, recordToUpsert, transaction, true);
      }
    }

    return;
  }

  // #endregion
  // region Check

  private checkIsTeamSoftDeleted(record: Team): boolean {
    return record.IsDeleted;
  }

  private checkImmutableFieldsMatch(a: Team, b: Team): boolean {
    return a.Id === b.Id && a.Name === b.Name;
  }

  // #endregion
  // #region Transform

  filterForUniqueTeams(teams: Team[]): Team[] {
    return teams.filter((project, index, self) => self.findIndex(p => p.Id === project.Id) === index);
  }

  sortTeamsByName(teams: Team[]): Team[] {
    return teams.sort((a,b) => (a.Name ?? '').localeCompare(b.Name ?? ''));
  }

  // #endregion
}
